import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react';
import clsx from 'clsx';
import type { SyntheticEvent } from 'react';
import type { StoreProductVariant } from 'components/productContext/types/storeProduct';
import type { Maybe } from 'components/storeContext/storeMachine';
import globalStyles from 'styles/globalStyles';

import styles from './ProductVariantSelector.module.scss';

export type ProductVariantSelectorType = {
  variants: StoreProductVariant[];
  selectedVariant: Maybe<StoreProductVariant>;
  selectVariant(variant: StoreProductVariant): void;
  proCustomer: boolean;
  isLoading: boolean;
  namespace: string;
};

export const ProductVariantSelector = ({
  variants,
  selectedVariant,
  selectVariant,
  proCustomer,
  isLoading,
  namespace,
}: ProductVariantSelectorType) => {
  const idPrefix = 'variantSelect';

  const id = namespace ? `${idPrefix}-${namespace}` : idPrefix;

  const onSelectVariant = (event: SyntheticEvent<HTMLSelectElement>) => {
    const variant = variants.find(
      (variant) => variant.id === event.currentTarget.value
    );

    if (variant) selectVariant(variant);
  };

  return (
    <>
      <label
        className={clsx(
          styles.variantSelector__label,
          'text-utility-utility-small',
          isLoading && globalStyles.isLoading
        )}
        htmlFor={id}
      >
        {selectedVariant?.selectedOptions[0].name ?? (
          <Trans id="product.selectOption" message="Select Option" />
        )}
      </label>

      <select
        id={id}
        className={clsx(
          styles.variantSelector,
          'text-body-small',
          isLoading && globalStyles.isLoading
        )}
        data-testid="variantSelect"
        name="variant"
        onChange={onSelectVariant}
        defaultValue={selectedVariant ? selectedVariant.id : undefined}
      >
        {isLoading && (
          <option key="loading" value="loading">
            <Trans id="accessibility.loading" message="Loading..." />
          </option>
        )}

        {!isLoading &&
          variants.map((variant) => {
            const proOnly = variant.proOnly && !proCustomer;
            return (
              <option
                key={variant.id}
                value={variant.id}
                data-test-id={variant.proOnly ? 'proOnlyOption' : undefined}
              >
                {variant.title}
                {proOnly && ` - ${i18n._(`Pro Only`)}`}
              </option>
            );
          })}
      </select>
    </>
  );
};
